// The Map tab: catch markers, map style menu, "show only the map" (eye button) and your location.
import { $, el, toast } from "../dom.ts";
import { fmtNum, fmtWhen, t } from "../i18n.ts";
import { photoUrl } from "../photos.ts";
import { ROUGH_M, getPosition, type Position } from "../position.ts";
import { sizeLine, speciesName } from "../species.ts";
import { onRender } from "../state.ts";
import { filtered } from "../list.ts";
import { openDetail } from "../detail.ts";
import { mapIsReady } from "./ready.ts";
import { FINLAND, LAYERS, LAYER_ORDER, depthLayers, depthOn, hasLeaflet, lastLoc, layerName, pinIcon, setDepthOn, setLayerName, tileLayer, type LayerName } from "./layers.ts";

/** The big map, created the first time the Map tab opens. */
export let bigMap: L.Map | null = null;
let pinLayer: L.LayerGroup | null = null, baseLayer: L.TileLayer | null = null, depthLayer: L.LayerGroup | null = null;
let meMarker: L.Marker | null = null, meCircle: L.Circle | null = null, fittedOnce = false;

export function showMap(): void {
  if (!hasLeaflet()) { $("#bigmap").replaceChildren(el("div", { class: "empty", style: "margin:16px" }, el("p", { text: t("mapOffline") }))); return; }
  if (!bigMap) {
    const start = lastLoc();
    const map = L.map("bigmap", { zoomControl: false, attributionControl: true }).setView(start ?? FINLAND, start ? 11 : 5);
    bigMap = map;
    if (!matchMedia("(pointer: coarse)").matches) L.control.zoom({ position: "bottomright" }).addTo(map);
    // the credits shrink to a © that opens on tap
    map.attributionControl.getContainer()?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).tagName !== "A") (e.currentTarget as HTMLElement).classList.toggle("open");
    });
    baseLayer = tileLayer(layerName).addTo(map);
    depthLayer = depthLayers();
    if (depthOn) depthLayer.addTo(map);
    pinLayer = L.layerGroup().addTo(map);
    drawPins();
    mapIsReady(map);
  }
  const map = bigMap;
  requestAnimationFrame(() => { map.invalidateSize(); if (!fittedOnce) { fitAll(); fittedOnce = true; } });
}

function drawPins(): void {
  if (!pinLayer) return;
  pinLayer.clearLayers();
  for (const c of filtered()) {
    if (!c.loc) continue;
    const mk = L.marker([c.loc.lat, c.loc.lon], { icon: pinIcon(c, null), title: speciesName(c) }).addTo(pinLayer);
    const firstPhoto = c.photoIds?.[0];
    if (firstPhoto) void photoUrl(firstPhoto).then((u) => u && mk.setIcon(pinIcon(c, u)));
    mk.bindPopup(() => {
      const img = el("img", { alt: "" }); img.hidden = true;
      if (firstPhoto) void photoUrl(firstPhoto).then((u) => { if (u) { img.src = u; img.hidden = false; } });
      return el("div", { class: "pop" }, img, el("div", {},
        el("b", { text: speciesName(c) }), el("small", { class: "num", text: [sizeLine(c), fmtWhen(c.time)].filter(Boolean).join(" · ") }),
        el("div", {}, el("button", { class: "btn small", type: "button", onclick: () => openDetail(c.id) }, t("open")))));
    });
  }
}
onRender(drawPins);

function fitAll(): void {
  if (!bigMap) return;
  const pts = filtered().flatMap((c) => (c.loc ? [[c.loc.lat, c.loc.lon] as L.LatLngTuple] : []));
  if (pts.length === 1) bigMap.setView(pts[0], 13);
  else if (pts.length) bigMap.fitBounds(pts, { padding: [50, 50], maxZoom: 14 });
}
$("#map-fit").addEventListener("click", fitAll);

/* ---------- map style list ---------- */
$("#map-layer").addEventListener("click", (e) => { e.stopPropagation(); toggleLayerMenu(); });
export function toggleLayerMenu(force?: boolean): void {
  const m = $("#layer-menu"), btn = $("#map-layer"), open = force ?? m.hidden;
  if (!open || !bigMap) { m.hidden = true; btn.setAttribute("aria-expanded", "false"); btn.setAttribute("aria-pressed", "false"); return; }
  m.replaceChildren(el("div", { class: "lmtitle", text: t("mapStyles") }), ...LAYER_ORDER.map((n) =>
    el("button", { class: "lmitem", type: "button", role: "menuitemradio", "aria-checked": String(n === layerName),
      onclick: () => { setBaseLayer(n); toggleLayerMenu(false); } },
      el("span", { class: "lmcheck", "aria-hidden": "true" }),
      el("span", { class: "lmtext" }, el("b", { text: t(LAYERS[n]!.name) }), el("small", { text: t(LAYERS[n]!.desc) })))),
    el("div", { class: "lmsep" }),
    el("label", { class: "lmtoggle" }, el("span", { class: "lmtext" }, el("b", { text: t("depthTitle") }), el("small", { text: t("depthDesc") })),
      el("input", { type: "checkbox", class: "switch", checked: depthOn, onchange: (e) => setDepth(e.currentTarget.checked) })));
  m.style.top = btn.offsetTop + (btn.parentElement?.offsetTop ?? 0) + "px";
  m.hidden = false;
  btn.setAttribute("aria-expanded", "true");
  btn.setAttribute("aria-pressed", "true");
}
document.addEventListener("click", (e) => { const m = $("#layer-menu"); if (!m.hidden && !m.contains(e.target as Node)) toggleLayerMenu(false); });
document.addEventListener("keydown", (e) => { if (e.key === "Escape") toggleLayerMenu(false); });

export function setBaseLayer(name: LayerName): void {
  setLayerName(name);
  if (bigMap && baseLayer) { bigMap.removeLayer(baseLayer); baseLayer = tileLayer(name).addTo(bigMap); }
}
function setDepth(on: boolean): void {
  setDepthOn(on);
  if (bigMap && depthLayer) { if (on) depthLayer.addTo(bigMap); else depthLayer.remove(); }
  showDepthDot();
}
/** A small dot on the map style button while water depths are on, so it shows without opening the menu. */
const showDepthDot = () => $("#map-layer").classList.toggle("dot", depthOn);
showDepthDot();

/* ---------- eye button ---------- */
// Hides every button, the filters, the wind box and the tab bar, so only the map shows (on Android also the browser
// bars, via the Fullscreen API; iPhone ignores that part). Tap again to bring everything back.
let mapFull = false;
export function setMapFull(on: boolean): void {
  if (on === mapFull) return;
  mapFull = on;
  $(".app").classList.toggle("map-full", on);
  $("#v-map").classList.toggle("clean", on);
  if (on) toggleLayerMenu(false);
  const b = $("#map-full");
  b.setAttribute("aria-label", t(on ? "mapShow" : "mapHide"));
  b.setAttribute("aria-pressed", String(on));
  b.querySelector("use")?.setAttribute("href", on ? "#i-eye" : "#i-eyeoff");
  const root = document.documentElement;
  if (on && root.requestFullscreen && !document.fullscreenElement) root.requestFullscreen({ navigationUI: "hide" }).catch(() => {});
  if (!on && document.fullscreenElement) document.exitFullscreen().catch(() => {});
  if (bigMap) { const map = bigMap; requestAnimationFrame(() => map.invalidateSize()); }
}
$("#map-full").addEventListener("click", () => setMapFull(!mapFull));
document.addEventListener("fullscreenchange", () => { if (!document.fullscreenElement && mapFull) setMapFull(false); });

/* ---------- your location ---------- */
// The button is on (blue) while your location shows; tapping it then hides the dot again.
$("#map-locate").addEventListener("click", async (e) => {
  const btn = e.currentTarget as HTMLElement;
  if (!bigMap) return;
  if (btn.classList.contains("busy")) return;
  if (meMarker) { meMarker.remove(); meCircle?.remove(); meMarker = meCircle = null; btn.setAttribute("aria-pressed", "false"); return; }
  // a spinning ring while the phone finds you (it can take a while outdoors), and a note if it takes longer
  btn.classList.add("busy"); btn.setAttribute("aria-busy", "true");
  const slow = setTimeout(() => toast(t("locFinding")), 1500);
  // the dot shows at the first fix with a circle for how uncertain it is; both follow as GPS sharpens the fix
  const map = bigMap;
  let first = true;
  const show = (p: Position) => {
    if (!meMarker) {
      meMarker = L.marker([p.lat, p.lon], { icon: L.divIcon({ html: el("div", { class: "me-dot" }), className: "", iconSize: [18, 18], iconAnchor: [9, 9] }), title: t("you") }).addTo(map);
      meCircle = L.circle([p.lat, p.lon], { radius: p.acc, className: "me-acc", weight: 1 }).addTo(map);
    } else { meMarker.setLatLng([p.lat, p.lon]); meCircle?.setLatLng([p.lat, p.lon]).setRadius(p.acc); }
    if (first || !map.getBounds().contains([p.lat, p.lon])) map.setView([p.lat, p.lon], Math.max(map.getZoom(), p.acc > 1000 ? 11 : 13));
    first = false;
    btn.setAttribute("aria-pressed", "true");
  };
  try {
    const p = await getPosition({ onFix: show });
    if (p.acc > ROUGH_M) toast(t("locRough", p.acc >= 1000 ? fmtNum(p.acc / 1000, 1) + " km" : p.acc + " m"));
  } catch { toast(t("locationOff")); }
  finally { clearTimeout(slow); btn.classList.remove("busy"); btn.removeAttribute("aria-busy"); }
});
